
"use client";

import Image from "next/image";
import { QuarterlyPricingCard } from "@/components/QuarterlyPricingCard";
import {subscriptionOffers,subscriptionMoney} from "@/lib/subscription-display";
import { HOME_DEFAULTS, type HomepageContent } from "@/lib/site-content";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import {
  PICKUP_DELIVERY_FEE_CENTS,
  WASH_CUSTOMER_RATE_PER_LB,
  WASH_MINIMUM_LBS,
  getWashCustomerChargeCents,
} from "@/lib/laundry";

// Zero-signup estimator — a lbs slider quoting the real pay-as-you-go total
// ($1.85/lb with the 12 lb minimum, plus the $8.50 pickup & delivery fee), all
// read from lib/laundry so the landing page can never drift from billing.
const ESTIMATOR_MIN_LBS = WASH_MINIMUM_LBS; // 12
const ESTIMATOR_MAX_LBS = 40;

function LoadEstimator({ pickupHref }: { pickupHref: string }) {
  const [lbs, setLbs] = useState(18);
  const totalCents = getWashCustomerChargeCents(lbs) + PICKUP_DELIVERY_FEE_CENTS;
  const total = `$${(totalCents / 100).toFixed(2)}`;
  return (
    <div
      data-animate
      className="mt-10 rounded-2xl border border-[#E3E8F0] bg-[#F5F8FC] p-6 sm:p-7"
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-10">
        <div className="lg:w-[38%]">
          <h4 className="text-lg font-semibold text-[#1F1B2E]">
            What would your load cost?
          </h4>
          <p className="mt-1.5 text-sm leading-relaxed text-[#4A5468]">
            Drag to your bag size. No signup needed, this is the real
            pay-as-you-go math.
          </p>
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
            <div>
              <span className="text-3xl font-bold leading-none text-[#1F1B2E]">
                {lbs} lbs
              </span>
              <p className="mt-1 text-xs text-[#4A5468]">
                A typical hamper is <span className="whitespace-nowrap">12 to 18 lbs.</span>
              </p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold leading-none text-[#3D69B0]">
                {total}
              </span>
              <p className="mt-1 text-xs text-[#4A5468]">
                <span className="whitespace-nowrap">{lbs} lb &times; ${WASH_CUSTOMER_RATE_PER_LB.toFixed(2)}</span>{" "}
                <span className="inline-block">+ ${(PICKUP_DELIVERY_FEE_CENTS / 100).toFixed(2)}{" "}
                  <span className="whitespace-nowrap">pickup &amp; delivery</span>
                </span>
              </p>
            </div>
          </div>
          <input
            type="range"
            min={ESTIMATOR_MIN_LBS}
            max={ESTIMATOR_MAX_LBS}
            step={1}
            value={lbs}
            onChange={(event) => setLbs(Number(event.target.value))}
            aria-label={`Estimated bag weight in pounds, ${ESTIMATOR_MIN_LBS} to ${ESTIMATOR_MAX_LBS}`}
            className="mt-4 w-full accent-[#3D69B0]"
          />
          <div className="mt-1 flex justify-between text-[11px] font-medium text-[#4A5468]">
            <span>{ESTIMATOR_MIN_LBS} lbs (minimum)</span>
            <span>{ESTIMATOR_MAX_LBS} lbs</span>
          </div>
        </div>
        <div className="lg:w-auto">
          <Link
            href={pickupHref}
            className="cta-magnetic group inline-flex items-center gap-2.5 rounded-full bg-[#F2B705] py-3.5 pl-6 pr-2.5 text-sm font-bold text-[#2E2404] shadow-[0_14px_30px_rgba(242,183,5,0.3)] hover:bg-[#F6C23D]"
          >
            Book this pickup
            <span className="cta-arrow inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#2E2404]/15" aria-hidden>&rarr;</span>
          </Link>
          <p className="mt-2.5 max-w-[200px] text-xs leading-relaxed text-[#4A5468]">
            Estimate only. Final price is by measured weight after pickup.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Home({ content = HOME_DEFAULTS }: { content?: HomepageContent }) {
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [navTone, setNavTone] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [marqueePaused, setMarqueePaused] = useState(false);
  // House/ambassador code from ?code= — when present we emphasize the primary
  // "Schedule a pickup" CTA and carry the code into the funnel (/pickup?code=).
  const [houseCode, setHouseCode] = useState<string | null>(null);
  const navLinkClass =
    "relative transition-all after:absolute after:-bottom-2 after:left-0 after:h-[2px] after:w-0 after:bg-current after:transition-all after:content-[''] hover:after:w-full hover:opacity-80";

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email ?? null);
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const code = new URLSearchParams(window.location.search).get("code");
    queueMicrotask(() => setHouseCode(code && code.trim() ? code.trim() : null));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("scroll") === "pickup") {
      const target = document.getElementById("pickup-cta");
      if (target) {
        target.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "center" });
      }
    }
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll("[data-animate]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const smoothScrollTo = (targetY: number) => {
    window.scrollTo({
      top: targetY,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
    });
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.history.pushState(null, "", `#${id}`);
    smoothScrollTo(el.getBoundingClientRect().top + window.scrollY - 88);
  };

  const scrollToTop = () => smoothScrollTo(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => {
      const heroHeight = heroRef.current?.offsetHeight ?? 1;
      const threshold = Math.max(heroHeight - 72, 1);
      const progress = Math.min(Math.max(window.scrollY / threshold, 0), 1);
      setNavTone(progress);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // Nav chrome morphs over a short eased band near the bottom of the hero so the
  // bar's background, border and text all shift together, rather than the text
  // fading the whole way down while the background snaps at a hard threshold.
  // navTone: 0 at the top of the page → 1 once the hero has scrolled away.
  const navBand = Math.min(Math.max((navTone - 0.72) / 0.28, 0), 1);
  const navEase = navBand * navBand * (3 - 2 * navBand); // smoothstep
  const mixNav = (from: number, to: number) =>
    Math.round(from + (to - from) * navEase);
  const navTextColor = `rgb(${mixNav(255, 31)}, ${mixNav(255, 27)}, ${mixNav(255, 46)})`;
  const navMutedColor = `rgba(${mixNav(255, 31)}, ${mixNav(255, 27)}, ${mixNav(255, 46)}, ${0.7 + navEase * 0.2})`;
  const navBackground = `rgba(${mixNav(31, 255)}, ${mixNav(27, 255)}, ${mixNav(46, 255)}, ${0.25 + navEase * 0.67})`;
  const navBorderColor = `rgba(${mixNav(255, 227)}, ${mixNav(255, 232)}, ${mixNav(255, 240)}, ${0.1 + navEase * 0.9})`;
  const navShadow = `0 8px 24px rgba(31, 27, 46, ${(0.07 * navEase).toFixed(3)})`;
  const navButtonColor = `rgb(${mixNav(255, 61)}, ${mixNav(255, 105)}, ${mixNav(255, 176)})`;
  const navButtonBg = `rgba(${mixNav(255, 238)}, ${mixNav(255, 244)}, ${mixNav(255, 255)}, ${0.2 + navEase * 0.8})`;
  // The mobile pickup pill is redundant while the hero (which has its own
  // CTA) is on screen — only reveal it once the hero has scrolled away.
  const heroPassed = navTone > 0.6;

  // When the visitor arrives via a house/ambassador code, the primary CTA
  // routes into the funnel carrying the code so the booking can pre-fill this
  // week's house pickup window. We also emphasize that CTA with a gold ring +
  // gentle pulse so it reads as "this is your next step".
  const hasHouseCode = Boolean(houseCode);
  const pickupHref = houseCode
    ? `/pickup?code=${encodeURIComponent(houseCode)}`
    : userEmail
      ? "/account?scroll=pickup"
      : "/pickup";
  // Brand-gold emphasis layered on top of the hero CTA's existing styles.
  const heroCtaEmphasis = hasHouseCode
    ? " scale-105 ring-2 ring-[#F2B705] ring-offset-2 ring-offset-[#1F1B2E] animate-pulse"
    : "";

  // ── Unified design system ───────────────────────────────────────────
  // ONE color family: navy (#1F1B2E heads / #4A5468 body / #E3E8F0 hairline),
  // blue (#3D69B0) structural, gold (#F2B705) reserved for CTAs + accents.
  // No purple/lavender anywhere — shadows are navy-tinted, not gray.
  const SECTION = "mx-auto w-full max-w-6xl px-6 py-20 sm:px-8 sm:py-28";
  // Display heading scale — heavier weight + tighter tracking for presence.
  const DISPLAY =
    "text-[2.25rem] font-bold leading-[1.05] tracking-tight text-[#1F1B2E] sm:text-[3.25rem]";
  const DISPLAY_LIGHT =
    "text-[2.25rem] font-bold leading-[1.05] tracking-tight text-white sm:text-[3.25rem]";



  const howSteps = content.steps.map((step,index) => ({...step,icon:["/assets/icon-calendar.png","/assets/icon-bag.png","/assets/icon-shirt.png"][index % 3]}));

  const pricingPlans = [
    { id: "dry-cleaning", name: "Dry Cleaning", price: "$9", unit: "/item", perks: ["Garment care", "Specialty fabrics", "Stain-safe handling"], pricePrefix: "Starting from", finePrint: "Price depends on item type. Dry-cleaning-only orders under 3 items have a $4 pickup & delivery fee." },
    { id: "payg", name: "Pay As You Go", price: subscriptionMoney(WASH_CUSTOMER_RATE_PER_LB*100), unit: "/lb", perks: [`${WASH_MINIMUM_LBS} lb minimum per bag`, "Charged by measured weight after pickup"], finePrint: `${WASH_MINIMUM_LBS} lb minimum per bag. ${subscriptionMoney(PICKUP_DELIVERY_FEE_CENTS)} pickup & delivery fee per order.`, featured: true },
  ];

  // Display the actual billed interval and allowance, without comparing unequal loads.
  const subscriptionPlans = subscriptionOffers().map(plan => ({
    id: `sub-${plan.name.toLowerCase()}`, name: plan.name,
    price: subscriptionMoney(plan.price_at_min_cents), cadence: plan.cadence,
    billed: `Billed every ${plan.cadence === "quarter" ? "3 months" : plan.cadence}`,
    bg: {week:"#3D69B0",month:"#2F5792",quarter:"#243A6B"}[plan.cadence],
    popular: plan.cadence === "quarter",
    perks: [
      `${plan.min_lbs} lbs covered each ${plan.cadence}`,
      "Pickup & delivery included",
      `Add lbs in ${plan.step_lbs}-lb steps at ${subscriptionMoney(plan.per_lb_rate_cents)}/lb`,
      "Unused lbs roll over (up to one period's allowance)",
    ],
    detail: `Overage: ${subscriptionMoney(plan.per_lb_rate_cents)}/lb after your available balance is used.`,
  }));


  const faqItems = content.faqs;

  const testimonials = [
    { name: "Nick (Freshman)", quote: "I needed my suit dry cleaned for coffee chats without a car. B-Laundry saved me and had it ready for my final round." },
    { name: "Amelie (Senior)", quote: "Everything was clean, nicely folded, and on time. Wish they had this two years ago." },
    { name: "Muhammod (Sophomore)", quote: "Engineering gets busy. B-Laundry did a great job and turned it around quickly." },
    { name: "Saanvi (Freshman)", quote: "No complaints. I'll order again next week." },
    { name: "Leagilwe (Junior)", quote: "Impressed. Pickup was smooth and the clean smell is solid. A scent option would be cool." },
    { name: "Chris (Freshman)", quote: "I was skeptical, but this beats washing it yourself at Hedrick." },
    { name: "Minjun (Sophomore)", quote: "Good laundry, nice and simple." },
  ];
  return (
    <div className="min-h-screen bg-white text-[#1F1B2E]">
      <SiteHeader
        position="fixed"
        onBrandClick={scrollToTop}
        hideTaglineOnMobile
        tone={{
          background: navBackground,
          borderColor: navBorderColor,
          boxShadow: navShadow,
          text: navTextColor,
          muted: navMutedColor,
        }}
        tagline={
          <>
            In Partnership with <span className="notranslate">Fold</span>
          </>
        }
        actions={
          <>
            {/* Mobile pickup CTA — fades in once the hero (and its own CTA) has
                scrolled away, so the primary action is always reachable below
                the fold without duplicating the hero button at the top. */}
            <Link
              href={userEmail ? "/account?scroll=pickup" : "/pickup"}
              aria-hidden={!heroPassed}
              tabIndex={heroPassed ? undefined : -1}
              className="inline-flex h-11 shrink-0 items-center whitespace-nowrap rounded-full bg-[#F2B705] px-4 text-[13px] font-bold text-[#2E2404] shadow-[0_8px_18px_rgba(242,183,5,0.35)] transition-all duration-300 hover:bg-[#F6C23D] md:hidden"
              style={{
                opacity: heroPassed ? 1 : 0,
                transform: heroPassed ? "translateY(0)" : "translateY(-6px)",
                pointerEvents: heroPassed ? "auto" : "none",
              }}
            >
              Schedule
            </Link>
            {/* Mobile sign-in button */}
            <Link
              href={userEmail ? "/account" : "/signin"}
              className="inline-flex h-11 shrink-0 items-center whitespace-nowrap rounded-md px-3 text-xs font-bold uppercase tracking-[0.06em] backdrop-blur-sm transition-colors hover:brightness-95 md:hidden"
              style={{ color: navButtonColor, backgroundColor: navButtonBg }}
            >
              {userEmail ? "Account" : "Sign in"}
            </Link>
            <nav
              className="hidden items-center gap-5 text-sm font-medium uppercase tracking-[0.12em] md:flex"
              style={{ color: navTextColor }}
            >
              <a
                className={navLinkClass}
                href="#how-it-works"
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection("how-it-works");
                }}
              >
                How it works
              </a>
              <a
                className={navLinkClass}
                href="#pricing"
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection("pricing");
                }}
              >
                Pricing
              </a>
              <a
                className={navLinkClass}
                href="#faq"
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection("faq");
                }}
              >
                FAQ
              </a>
              <Link
                href={userEmail ? "/account" : "/signin"}
                className="inline-flex h-9 items-center rounded-md border border-current/30 px-4 text-sm font-medium uppercase tracking-[0.12em] transition-all hover:bg-white/15"
              >
                {userEmail
                  ? "Account"
                  : "Sign-in"}
              </Link>
              {/* Gold pill in the nav — the primary action is always one click
                  away no matter how far the user has scrolled. */}
              <Link
                href={userEmail ? "/account?scroll=pickup" : "/pickup"}
                className="cta-magnetic inline-flex h-9 items-center gap-1.5 rounded-full bg-[#F2B705] px-4 text-sm font-bold normal-case tracking-normal text-[#2E2404] shadow-[0_8px_18px_rgba(242,183,5,0.35)] hover:bg-[#F6C23D]"
              >
                Schedule a pickup
                <span className="cta-arrow" aria-hidden>&rarr;</span>
              </Link>
            </nav>
            <a
              href="https://www.linkedin.com/company/b-laundry/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="B-Laundry on LinkedIn"
              className="hidden h-10 w-10 items-center justify-center rounded-md transition-all hover:bg-white/15 md:inline-flex"
              style={{ color: navTextColor }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.268 2.37 4.268 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
          </>
        }
      />

      <main id="main-content" className="w-full pb-3">
        <section
          ref={heroRef}
          className="relative min-h-[82vh] w-full overflow-hidden sm:min-h-[88vh]"
        >
          <Image
            src={content.hero.imagePath}
            alt={content.hero.imageAlt}
            fill
            priority
            sizes="100vw"
            className="object-cover"
            style={{ objectPosition: "center 35%" }}
          />
          {/* The single permitted gradient: one diagonal navy scrim, anchored
              bottom-left, so the editorial left-aligned headline stays legible
              over the photo. Everything else on the page is solid color. */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#1F1B2E]/90 via-[#1F1B2E]/45 to-transparent" />
          {/* Slim top wash purely for nav legibility over bright sky. */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#1F1B2E]/55 to-transparent" />
          {/* Bottom scrim — keeps the hero copy legible over bright photo
              regions (notably on iOS Safari, where the crop sits higher). */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#1F1B2E]/85 via-[#1F1B2E]/30 to-transparent" />

          {/* Left-aligned editorial hero — breaks the centered-everything tell. */}
          <div className="absolute inset-0 mx-auto flex w-full max-w-6xl flex-col items-start justify-end px-6 pb-16 sm:px-8 sm:pb-24">
            <div
              className="hero-line-1 flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.24em] text-[#F2B705]"
              style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.95)) drop-shadow(0 2px 12px rgba(0,0,0,0.6))" }}
            >
              <span className="h-px w-7 bg-[#F2B705]" />
              {content.hero.eyebrow}
            </div>
            <h1
              className="hero-line-2 mt-4 max-w-[16ch] font-bold tracking-tight text-white"
              style={{ fontSize: "clamp(44px, 9vw, 80px)", lineHeight: 0.98, textShadow: "0 2px 40px rgba(0,0,0,0.5)" }}
            >
              {content.hero.title}
            </h1>
            <p
              className="hero-line-3 mt-5 max-w-lg text-base leading-relaxed text-white sm:text-lg"
              style={{ textShadow: "0 1px 3px rgba(0,0,0,0.95), 0 2px 18px rgba(0,0,0,0.6)" }}
            >
              {content.hero.description}
            </p>
            <div className="hero-cta mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Link
                id="pickup-cta"
                href={pickupHref}
                className={`cta-magnetic group inline-flex items-center gap-2.5 rounded-full bg-[#F2B705] py-4 pl-7 pr-3 text-base font-bold text-[#2E2404] shadow-[0_16px_36px_rgba(242,183,5,0.5)] transition-transform hover:-translate-y-0.5 hover:bg-[#F6C23D]${heroCtaEmphasis}`}
              >
                {content.hero.ctaLabel}
                <span className="cta-arrow inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2E2404]/15" aria-hidden>
                  &rarr;
                </span>
              </Link>
              <a
                href="#how-it-works"
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection("how-it-works");
                }}
                className="text-sm font-semibold text-white underline-offset-4 transition-colors hover:text-white hover:underline"
              >
                See how it works
              </a>
            </div>
          </div>
        </section>

        {/* ── How it works — FULL-BLEED NAVY ────────────────────────────
            The decisive dark block. Strong white-on-navy contrast, an
            editorial left header column, and oversized gold numerals with a
            connecting hairline instead of three identical white cards. */}
        <section id="how-it-works" className="relative w-full scroll-mt-24 bg-[#1F1B2E] text-white">
          <div className="mx-auto w-full max-w-6xl px-6 py-20 sm:px-8 sm:py-28">
            <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
              {/* Left: editorial header column */}
              <div className="lg:self-start">
                <h2 data-animate className={DISPLAY_LIGHT}>
                  <span className="whitespace-pre-line">{content.howTitle}</span>
                </h2>
                <p data-animate data-delay="2" className="mt-5 max-w-sm text-base leading-relaxed text-white">
                  {content.howDescription}
                </p>
                <Link
                  data-animate
                  data-delay="3"
                  href={userEmail ? "/account?scroll=pickup" : "/pickup"}
                  className="cta-magnetic group mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#F2B705] py-3.5 pl-6 pr-2.5 text-sm font-bold text-[#2E2404] shadow-[0_14px_30px_rgba(242,183,5,0.3)] hover:bg-[#F6C23D]"
                >
                  Schedule a pickup
                  <span className="cta-arrow inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#2E2404]/15" aria-hidden>&rarr;</span>
                </Link>
              </div>

              {/* Right: numbered steps, divided by hairlines (no card boxes) */}
              <ol className="flex flex-col">
                {howSteps.map((step, index) => (
                  <li
                    key={step.icon}
                    data-animate
                    data-delay={String(index + 2) as "2" | "3" | "4"}
                    className="group flex items-start gap-6 border-t border-white/12 py-8 first:border-t-0 first:pt-0 sm:gap-8"
                  >
                    <span className="step-numeral shrink-0 font-bold leading-none text-white/15 transition-colors group-hover:text-[#F2B705]/80" style={{ fontSize: "clamp(40px, 7vw, 64px)" }}>
                      {index + 1}
                    </span>
                    <div className="flex flex-1 items-start gap-4 pt-1 sm:gap-5">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/8 ring-1 ring-white/10">
                        <Image src={step.icon} alt="" width={40} height={40} className="h-9 w-9 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-white">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* ── Our cleaning partner: Fold ─────────────────────────────────
            Clears up the #1 misunderstanding: B-Laundry does pickup +
            delivery; the actual washing/dry cleaning is done by Fold, a
            5-star professional cleaner. Light band for rhythm between the
            navy how-it-works and the white pricing section. */}
        <section className="w-full bg-[#F5F8FC]">
          <div className={SECTION}>
            <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
              <div>
                <h2 data-animate className={DISPLAY}>
                  The cleaning is done by Fold.
                </h2>
                <p data-animate data-delay="2" className="mt-5 max-w-xl text-base leading-relaxed text-[#4A5468]">
                  We handle the pickup and delivery. The actual washing and dry cleaning is done by <span className="font-semibold text-[#1F1B2E]">Fold</span>, a professional 5-star-rated laundry and dry cleaner serving LA. Your clothes are never mixed with anyone else&apos;s: they&apos;re sorted by color and fabric, cleaned in commercial-grade machines tuned to each fabric type, washed with eco-friendly detergent and a double rinse, then folded or pressed by experienced pros.
                </p>
                <p data-animate data-delay="3" className="mt-5 flex items-start gap-2.5 max-w-xl text-sm leading-relaxed text-[#4A5468]">
                  <svg className="mt-0.5 shrink-0 text-[#3D69B0]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                  <span>Every bag is tagged to you and tracked from pickup to delivery. If anything&apos;s off, message us and we&apos;ll make it right.</span>
                </p>
              </div>

              {/* Real photo of the careful cleaning; Fold's rating rides as an overlaid badge */}
              <div data-animate data-delay="2" className="relative overflow-hidden rounded-2xl shadow-[0_18px_40px_rgba(31,27,46,0.14)]">
                <div className="relative aspect-[4/3]">
                  <Image
                    src="/assets/care-hang-drying.webp"
                    alt="Knitwear and sweaters hang-drying with care at Fold"
                    fill
                    sizes="(max-width: 1024px) 100vw, 45vw"
                    className="object-cover"
                  />
                </div>
                <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-3.5 py-2 shadow-lg ring-1 ring-black/5 backdrop-blur-sm">
                  <span className="text-base font-bold leading-none text-[#1F1B2E]">4.9</span>
                  <span className="leading-none text-[#F2B705]">★</span>
                  <span className="text-xs font-semibold text-[#4A5468]">Fold rating</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Pricing — two clearly-separated groups ────────────────────
            Left-aligned editorial header, then GROUP A (pay per use) as a
            scannable 2-up, then GROUP B (subscribe) as three navy tiers with
            the middle one keylined gold as "Most popular". */}
        <section id="pricing" className="mx-auto w-full max-w-6xl px-6 pt-20 pb-12 sm:px-8 sm:pt-28 sm:pb-14 scroll-mt-24">
          <div className="max-w-2xl">
            <h2 data-animate className={DISPLAY}>
              Priced for students.
            </h2>
            <p data-animate data-delay="2" className="mt-5 text-base leading-relaxed text-[#4A5468]">
              Pay as you go when you need us, or choose a plan with pickup and delivery included.
            </p>
            <p data-animate data-delay="3" className="mt-3 text-sm font-medium text-[#3D69B0]">
              New here? Start pay-as-you-go; subscribe once you&apos;re doing laundry weekly.
            </p>
          </div>

          {/* GROUP A — Pay per use */}
          <div className="mt-14">
            <h3 data-animate className="text-sm font-bold uppercase tracking-[0.16em] text-[#1F1B2E]">
              Pay per use
            </h3>
            <div className="mt-5 grid gap-5 lg:grid-cols-5">
              {/* Featured: Pay as you go — gold keyline, takes 3 of 5 cols */}
              <Link
                data-animate
                data-delay="1"
                href={userEmail ? "/account?scroll=pickup" : "/pickup"}
                className="cta-magnetic draw-checks group relative flex flex-col overflow-hidden rounded-2xl p-7 ring-1 ring-white/10 hover:-translate-y-1 lg:col-span-3 lg:flex-row lg:items-center lg:gap-8"
                style={{ background: "#3D69B0", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 8px rgba(31,27,46,0.12), 0 20px 44px rgba(31,27,46,0.24)" }}
              >
                <div className="flex-1">
                  <span className="inline-flex rounded-md bg-black/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                    Wash &amp; Fold · No commitment
                  </span>
                  <div className="mt-3 text-lg font-bold text-white">Pay as you go</div>
                  <div className="mt-3 flex items-end gap-1.5">
                    <span className="text-[3rem] font-bold leading-none text-white">{subscriptionMoney(WASH_CUSTOMER_RATE_PER_LB*100)}</span>
                    <span className="mb-1.5 text-base text-white">/lb</span>
                  </div>
                  <p className="mt-2 text-[13px] font-bold text-white">About 30% below the average LA wash &amp; fold rate.</p>
                  <p className="mt-1 text-sm text-white">plus {subscriptionMoney(PICKUP_DELIVERY_FEE_CENTS)} pickup &amp; delivery per order</p>
                  <p className="mt-2 text-xs text-white">Minimum wash order: {subscriptionMoney(getWashCustomerChargeCents(WASH_MINIMUM_LBS)+PICKUP_DELIVERY_FEE_CENTS)} for a {WASH_MINIMUM_LBS} lb bag, delivered.</p>
                </div>
                <div className="mt-6 lg:mt-0 lg:w-[46%] lg:border-l lg:border-white/15 lg:pl-8">
                  <ul className="space-y-2.5 text-sm text-white">
                    {pricingPlans[1].perks.map((perk, i) => (
                      <li key={`payg-${i}`} className="flex items-start gap-2.5">
                        <svg style={{ color: "rgba(255,255,255,0.8)", marginTop: 3, flexShrink: 0 }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline className="check-mark" points="20 6 9 17 4 12" /></svg>
                        {perk}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-white">
                    Schedule a pickup <span className="cta-arrow" aria-hidden>&rarr;</span>
                  </span>
                </div>
              </Link>

              {/* Dry cleaning — deep gold card echoing our gold garment bags.
                  Darker goldenrod fill (#8C6510, ~5:1 with white) so white
                  text and checks stay legible. */}
              <Link
                data-animate
                data-delay="2"
                href="/dry-cleaning"
                className="card-lift group relative flex flex-col overflow-hidden rounded-2xl p-7 ring-1 ring-white/10 hover:-translate-y-1 lg:col-span-2"
                style={{ background: "#805C0D", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2), 0 2px 8px rgba(31,27,46,0.16), 0 20px 44px rgba(31,27,46,0.28)" }}
              >
                <span className="inline-flex w-fit rounded-md bg-black/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                  Dry Cleaning
                </span>
                <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white">Starting from</div>
                <div className="mt-1 flex items-end gap-1.5">
                  <span className="text-[3rem] font-bold leading-none text-white">$9</span>
                  <span className="mb-1.5 text-base text-white">/item</span>
                </div>
                <ul className="mt-5 flex-1 space-y-2.5 text-sm text-white">
                  {pricingPlans[0].perks.map((perk, i) => (
                    <li key={`dc-${i}`} className="flex items-start gap-2.5">
                      <svg style={{ color: "#FFFFFF", marginTop: 3, flexShrink: 0 }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline className="check-mark" points="20 6 9 17 4 12" /></svg>
                      {perk}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs leading-relaxed text-white">{pricingPlans[0].finePrint}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-white">
                  View full price list <span className="transition-transform group-hover:translate-x-0.5" aria-hidden>&rarr;</span>
                </span>
              </Link>
            </div>

            {/* Zero-signup estimator — a concrete number before any commitment */}
            <LoadEstimator pickupHref={pickupHref} />
          </div>

          {/* GROUP B — Subscribe & save */}
          <div className="mt-16">
            <div data-animate className="flex flex-wrap items-end justify-between gap-2">
              <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-[#1F1B2E]">
                Subscriptions
              </h3>
              <p className="text-sm text-[#4A5468]">
                Pickup &amp; delivery included.
              </p>
            </div>

            <div className="mt-5 grid items-stretch gap-5 md:grid-cols-3">
              {subscriptionPlans.map((plan, idx) => {
                if (plan.cadence === "quarter") return <QuarterlyPricingCard key={plan.id} signedIn={Boolean(userEmail)} />;
                const popular = Boolean(plan.popular); // Quarterly = elevated pick
                return (
                  <Link
                    key={plan.id}
                    data-pricing-plan={plan.cadence}
                    data-animate
                    data-delay={String(idx + 1) as "1" | "2" | "3"}
                    href={userEmail ? "/account" : "/signin?redirect=account"}
                    className={`card-lift group relative flex h-full flex-col overflow-hidden rounded-2xl p-7 text-left hover:-translate-y-1 ${popular ? "ring-2 ring-[#F2B705] md:scale-[1.04] md:hover:-translate-y-1.5" : "ring-1 ring-white/10"}`}
                    style={{
                      background: plan.bg,
                      boxShadow: popular
                        ? "inset 0 1px 0 rgba(255,255,255,0.14), 0 26px 56px rgba(31,27,46,0.34)"
                        : "inset 0 1px 0 rgba(255,255,255,0.14), 0 20px 44px rgba(31,27,46,0.24)",
                    }}
                  >
                    {popular && (
                      <span className="mb-3 inline-flex w-fit rounded-md bg-[#F2B705] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#2E2404]">
                        Most popular with Bruins
                      </span>
                    )}
                    <div className="text-base font-bold uppercase tracking-wide text-white">
                      {plan.name}
                    </div>
                    <div className="mt-4 flex flex-wrap items-end gap-x-1.5 gap-y-2">
                      <span className="text-[2.25rem] font-bold leading-none text-white sm:text-[2.75rem]">{plan.price}</span>
                      <span className="mb-1.5 text-sm text-white">/{plan.cadence}</span>
                    </div>
                    <p className="mt-2 text-xs font-medium text-white">{plan.billed}</p>

                    <div className="my-5 h-px w-full bg-white/15" />

                    <ul className="flex-1 space-y-2.5 text-sm">
                      {plan.perks.map((perk, perkIdx) => (
                        <li key={`${plan.id}-perk-${perkIdx}`} className="flex items-start gap-2.5">
                          <svg style={{ color: "rgba(255,255,255,0.8)", marginTop: 3, flexShrink: 0 }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                            <polyline className="check-mark" points="20 6 9 17 4 12" />
                          </svg>
                          <span className="text-white">{perk}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-5 rounded-lg bg-black/10 px-3.5 py-2.5 text-xs font-medium leading-relaxed text-white">
                      {plan.detail}
                    </div>

                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-white">
                      Subscribe <span className="transition-transform group-hover:translate-x-0.5" aria-hidden>&rarr;</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Closing CTA — the freshly-folded result sits beside the ask */}
          <div data-animate className="mt-14 grid items-center gap-8 overflow-hidden rounded-2xl bg-[#1F1B2E] p-6 sm:p-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="text-center lg:text-left">
              <p className="text-lg font-semibold text-white sm:text-xl">Not sure which plan? Just book a pickup.</p>
              <p className="mx-auto mt-3 max-w-md text-sm text-white lg:mx-0">You can always switch to a subscription later from your account.</p>
              <Link
                href={userEmail ? "/account?scroll=pickup" : "/pickup"}
                className="cta-magnetic group mt-6 inline-flex items-center gap-2.5 rounded-full bg-[#F2B705] py-4 pl-7 pr-3 text-base font-bold text-[#2E2404] shadow-[0_16px_36px_rgba(242,183,5,0.4)] hover:-translate-y-0.5 hover:bg-[#F6C23D]"
              >
                Schedule a pickup
                <span className="cta-arrow inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2E2404]/15" aria-hidden>&rarr;</span>
              </Link>
              <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-white lg:justify-start">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                Secure checkout, payments by Stripe
              </p>
            </div>
            <div className="relative overflow-hidden rounded-xl shadow-[0_18px_40px_rgba(0,0,0,0.4)]">
              <div className="relative aspect-[4/3]">
                <Image
                  src="/assets/care-back-on-campus.webp"
                  alt="Freshly folded laundry wrapped and delivered on a windowsill overlooking UCLA"
                  fill
                  sizes="(max-width: 1024px) 100vw, 42vw"
                  className="object-cover"
                  style={{ objectPosition: "center 55%" }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ — editorial header + real accordion (hairline dividers) ── */}
        <section id="faq" className="w-full scroll-mt-24 bg-[#F5F8FC]">
          <div className="mx-auto w-full max-w-6xl px-6 pt-16 pb-20 sm:px-8 sm:pt-20 sm:pb-28">
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
              <div className="lg:self-start">
                <h2 data-animate className={DISPLAY}>
                  {content.faqTitle}
                </h2>
                <p data-animate data-delay="2" className="mt-5 max-w-xs text-base leading-relaxed text-[#4A5468]">
                  {content.faqDescription}
                </p>

                {/* Contact card — the Instagram gradient stays contained to the icon tile */}
                <a
                  data-animate
                  data-delay="3"
                  href="https://www.instagram.com/b_laundry_la"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Message B-Laundry on Instagram"
                  className="group mt-7 flex max-w-xs items-center gap-4 rounded-2xl border border-[#E3E8F0] bg-white p-4 shadow-[0_10px_30px_rgba(31,27,46,0.06)] transition-all hover:-translate-y-0.5 hover:border-[#CBD8EE] hover:shadow-[0_16px_38px_rgba(31,27,46,0.12)]"
                >
                  <span
                    aria-hidden
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-[0_6px_16px_-4px_rgba(214,41,118,0.55)]"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 107%, #FDF497 0%, #FDF497 5%, #FD5949 45%, #D6249F 60%, #285AEB 90%)",
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" className="transition-transform group-hover:rotate-[8deg]">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                    </svg>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#1F1B2E]">Still have a question?</span>
                    <span className="block truncate text-sm text-[#4A5468]">
                      DM us <span className="font-semibold text-[#1F1B2E]">@b_laundry_la</span>
                    </span>
                  </span>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0 text-[#B7C0D0] transition-all group-hover:translate-x-0.5 group-hover:text-[#3D69B0]">
                    <polyline points="9 6 15 12 9 18" />
                  </svg>
                </a>
                <p data-animate data-delay="4" className="mt-3 max-w-xs text-sm text-[#4A5468]">
                  Prefer email? Reach us at{" "}
                  <a
                    href="mailto:support@b-laundry.co"
                    className="font-semibold text-[#3D69B0] underline decoration-[#3D69B0]/30 underline-offset-2 transition-colors hover:decoration-[#3D69B0]"
                  >
                    support@b-laundry.co
                  </a>
                  .
                </p>
                <Link
                  data-animate
                  data-delay="5"
                  href={userEmail ? "/account?scroll=pickup" : "/pickup"}
                  className="cta-magnetic group mt-7 inline-flex items-center gap-2.5 rounded-full bg-[#F2B705] py-3.5 pl-6 pr-2.5 text-sm font-bold text-[#2E2404] shadow-[0_14px_30px_rgba(242,183,5,0.3)] hover:bg-[#F6C23D]"
                >
                  Schedule a pickup
                  <span className="cta-arrow inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#2E2404]/15" aria-hidden>&rarr;</span>
                </Link>
              </div>

              <div data-animate className="border-t border-[#E3E8F0]">
                {faqItems.map((item, index) => {
                  const isOpen = openFaq === index;
                  return (
                    <div key={`faq-item-${index}`} className="border-b border-[#E3E8F0]">
                      <h3 className="m-0">
                        <button
                          type="button"
                          id={`faq-trigger-${index}`}
                          onClick={() => setOpenFaq(isOpen ? null : index)}
                          aria-expanded={isOpen}
                          aria-controls={`faq-panel-${index}`}
                          className="flex w-full items-center justify-between gap-4 py-5 text-left text-base font-semibold text-[#1F1B2E] transition-colors hover:text-[#3D69B0]"
                        >
                          <span>{item.question}</span>
                          <svg className="faq-chevron shrink-0 text-[#3D69B0]" data-open={isOpen} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                      </h3>
                      <div
                        id={`faq-panel-${index}`}
                        role="region"
                        aria-labelledby={`faq-trigger-${index}`}
                        className="faq-panel"
                        data-open={isOpen}
                        inert={!isOpen}
                      >
                        <div>
                          <p className="pb-5 pr-8 text-sm leading-relaxed text-[#4A5468]">{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ── Testimonials — full-bleed drifting marquee (the one marquee) ─ */}
        <section className="w-full overflow-hidden py-20 sm:py-28">
          <div className="mx-auto mb-10 w-full max-w-6xl px-6 sm:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 data-animate data-delay="1" className={DISPLAY}>
                Students love us.
              </h2>
              <button
                type="button"
                onClick={() => setMarqueePaused((p) => !p)}
                aria-pressed={marqueePaused}
                className="inline-flex items-center gap-2 rounded-full border border-[#E3E8F0] bg-white px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] text-[#3D69B0] transition-colors hover:border-[#CBD8EE] hover:bg-[#F5F8FC]"
              >
                {marqueePaused ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M8 5v14l11-7z" /></svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M6 4h4v16H6zM14 4h4v16h-4z" /></svg>
                )}
                {marqueePaused ? "Play" : "Pause"}
                <span className="sr-only"> testimonial scroll</span>
              </button>
            </div>
          </div>

          <div data-animate className="marquee-mask relative w-full">
            {/* edge fades so cards drift in/out softly, not abruptly clipped */}
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-white to-transparent sm:w-28" aria-hidden />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-white to-transparent sm:w-28" aria-hidden />
            <div
              className="marquee-track flex gap-5"
              style={{ animationPlayState: marqueePaused ? "paused" : "running" }}
            >
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <figure
                  key={`testimonial-${index}`}
                  aria-hidden={index >= testimonials.length}
                  className="flex w-[300px] shrink-0 flex-col rounded-2xl border border-[#E3E8F0] bg-white p-6 text-left shadow-[0_12px_30px_rgba(31,27,46,0.06)] sm:w-[340px]"
                >
                  <span aria-hidden className="font-bold leading-none text-[#F2B705]" style={{ fontSize: "2.5rem" }}>&ldquo;</span>
                  <blockquote className="mt-1 flex-1 text-[15px] leading-relaxed text-[#3A4456]">
                    {testimonial.quote}
                  </blockquote>
                  <figcaption className="mt-5 text-sm font-bold text-[#1F1B2E]">
                    {testimonial.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ── In the press — replica of the Daily Bruin article page (no ads) ── */}
        <section id="press" className="mx-auto w-full max-w-6xl scroll-mt-24 px-6 pb-20 sm:px-8 sm:pb-28">
          <div className="flex flex-col items-center gap-10 lg:flex-row lg:items-center lg:gap-16">
            <a
              data-animate
              href="https://dailybruin.com/2026/04/29/time-costs-money-and-bruins-save-both-with-newly-launched-door-to-door-service-b-laundry"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Read the Daily Bruin feature on B-Laundry"
              className="group block w-full max-w-md overflow-hidden rounded-lg bg-white shadow-[0_18px_44px_rgba(31,27,46,0.16)] ring-1 ring-black/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_56px_rgba(31,27,46,0.22)] lg:shrink-0"
            >
              {/* Masthead — centered Playfair Display wordmark (the paper's typeface) */}
              <div className="relative px-5 py-3 sm:px-6 sm:py-3.5">
                <span className="absolute left-5 top-1/2 hidden -translate-y-1/2 text-[10px] font-bold text-[#1A1A1A] sm:block" style={{ fontFamily: "var(--font-playfair), Georgia, serif" }}>
                  Thursday, May 28, 2026
                </span>
                <div className="text-center text-[1.3rem] font-bold leading-none tracking-[0.02em] text-[#5C7FC2] sm:text-[1.5rem]" style={{ fontFamily: "var(--font-playfair), Georgia, serif" }}>
                  DAILY BRUIN
                </div>
                <div className="absolute right-5 top-1/2 hidden -translate-y-1/2 items-center gap-2.5 text-[#1A1A1A] lg:flex">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.9h-2.34V22c4.78-.76 8.44-4.92 8.44-9.94Z" /></svg>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden><path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.66l-5.21-6.82-5.97 6.82H1.68l7.73-8.84L1.25 2.25h6.83l4.71 6.23 5.45-6.23Zm-1.16 17.52h1.83L7.08 4.13H5.12L17.08 19.77Z" /></svg>
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden><path d="M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.64.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16Zm0 3.68A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84Zm0 10.16A4 4 0 1 1 16 12a4 4 0 0 1-4 4Zm6.41-11.85a1.44 1.44 0 1 0 1.44 1.44 1.44 1.44 0 0 0-1.44-1.44Z" /></svg>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 0 0 .5 6.2 31.6 31.6 0 0 0 0 12a31.6 31.6 0 0 0 .5 5.81 3.02 3.02 0 0 0 2.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 0 0 2.12-2.14A31.6 31.6 0 0 0 24 12a31.6 31.6 0 0 0-.5-5.81ZM9.55 15.57V8.43L15.82 12Z" /></svg>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                </div>
              </div>

              {/* Primary nav — single row, clipped with a fade like a real screenshot */}
              <div className="relative border-y border-black/15">
                <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap px-5 py-2 text-[9px] font-bold uppercase tracking-[0.03em] text-[#1A1A1A] sm:px-6">
                  {["News", "Sports", "Arts", "Opinion", "The Quad", "Photo", "Video", "Illustrations", "Cartoons", "Graphics", "The Stack", "Prime", "Enterprise", "Interactives", "Podcasts", "Games", "Classifieds", "Print Issues"].map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
                <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white to-transparent" />
              </div>

              {/* Article header */}
              <div className="px-5 pt-4 sm:px-6 sm:pt-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.06em]">
                  <span className="text-[#0074B5]">A Closer Look</span>
                  <span className="text-[#1A1A1A]">, </span>
                  <span className="text-[#0074B5]">Campus</span>
                  <span className="text-[#1A1A1A]">, </span>
                  <span className="text-[#0074B5]">News</span>
                </div>
                <p className="mt-2 text-[1.15rem] font-bold leading-[1.18] text-black sm:text-[1.35rem]" style={{ fontFamily: "var(--font-dm-serif), Georgia, serif" }}>
                  &lsquo;Time costs money,&rsquo; and Bruins save both with door-to-door service B-Laundry
                </p>
                {/* byline — sits under the headline, as on the article page */}
                <div className="mt-2.5 flex items-end justify-between gap-4">
                  <div className="text-xs leading-tight">
                    <span className="font-bold text-[#1A1A1A]">By </span>
                    <span className="font-bold text-[#0074B5]">Julian Duaybis</span>
                    <span className="mt-0.5 block text-[11px] text-[#646464]">April 29, 2026 11:02 a.m.</span>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-bold text-[#0074B5] transition-all group-hover:gap-2.5">
                    Read the full feature
                    <span className="cta-arrow" aria-hidden>&rarr;</span>
                  </span>
                </div>
              </div>

              {/* Lead image — wide snapshot crop, kept short so the card reads as a clip */}
              <div className="mt-3 px-5 pb-4 sm:px-6">
                <div className="relative aspect-[16/7] w-full overflow-hidden bg-[#C9A87C]">
                  <Image
                    src="/assets/daily-bruin-feature.webp"
                    alt="Illustration of a wardrobe with folded laundry on the shelf and freshly cleaned clothes on hangers"
                    fill
                    sizes="(max-width: 640px) 100vw, 36rem"
                    className="object-cover"
                    style={{ objectPosition: "center top" }}
                  />
                </div>
                <p className="pt-2 text-[11px] text-[#646464]">
                  (Susanne Soroushian/Daily Bruin staff)
                </p>
              </div>
            </a>

            {/* What this is — short explainer beside the clipping */}
            <div data-animate data-delay="1" className="max-w-sm text-center lg:text-left">
              <h2 className={DISPLAY}>We made the news.</h2>
              <p className="mt-4 text-base leading-relaxed text-[#4A5468]">
                The Daily Bruin, UCLA&rsquo;s student newspaper, featured how B-Laundry
                saves students time with door-to-door laundry and dry cleaning.
              </p>
            </div>
          </div>
        </section>

        {/* ── Team — light section, left-aligned header, re-skinned cards ── */}
        <section className="w-full scroll-mt-24 bg-[#F5F8FC]">
          <div className="mx-auto w-full max-w-6xl px-6 py-20 sm:px-8 sm:py-28">
          <div className="max-w-2xl">
            <h2 data-animate className={DISPLAY}>
              Built by students, for students.
            </h2>
            <p data-animate data-delay="2" className="mt-5 text-base leading-relaxed text-[#4A5468]">
              We&apos;re a small team right here on campus, making laundry one less thing you have to think about.
            </p>
          </div>

          {(() => {
            const management = [
              {
                name: "Vosse Plackis Cheng",
                role: "CEO",
                src: "/assets/team-vosse.jpeg",
                imgClass: "",
                linkedin: "https://www.linkedin.com/in/vosse-plackis-cheng-b00737180/",
              },
              {
                name: "Angelo Freese-Binder",
                role: "CFO",
                src: "/assets/team-angelo.jpeg",
                imgClass: "",
                linkedin: "https://www.linkedin.com/in/angelofreesebinder/",
              },
              {
                name: "Naaisha Garg",
                role: "CMO",
                src: "/assets/team-naaisha.jpeg",
                imgClass: "scale-[1.7] saturate-[1.25] brightness-[1.05]",
                linkedin: "https://www.linkedin.com/in/naaisha-garg-534a08282/",
              },
            ];
            const ambassadors = [
              {
                name: "Ione Chicoine",
                role: "Brand Ambassador",
                src: "/assets/team-ione.png",
                imgClass: "saturate-[1.25]",
                linkedin: "https://www.linkedin.com/in/ione-chicoine/",
              },
              {
                name: "Ava Schanzlin",
                role: "Brand Ambassador",
                src: "/assets/team-ava.png",
                imgClass: "scale-[1.15] -translate-y-[5%] saturate-[1.5] object-top",
                linkedin: "https://www.linkedin.com/in/ava-schanzlin/",
              },
            ];
            const renderCard = (member: typeof management[number]) => (
              <div
                key={member.name}
                data-animate
                className="group relative flex w-full flex-col items-center rounded-2xl border border-[#E3E8F0] bg-white px-6 py-8 shadow-[0_12px_30px_rgba(31,27,46,0.06)] transition-all duration-300 hover:-translate-y-1.5 hover:border-[#CBD8EE] hover:shadow-[0_24px_48px_rgba(31,27,46,0.12)] sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
              >
                {/* Soft blue bloom behind the headshot on hover — a branded glow. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute left-1/2 top-7 h-36 w-36 -translate-x-1/2 rounded-full bg-[#3D69B0]/12 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
                />
                {/* Squircle headshot with a gold keyline ring on hover — a small
                    branded detail instead of the generic perfect-circle avatar. */}
                <div className="relative h-36 w-36 overflow-hidden rounded-[1.75rem] ring-1 ring-[#E3E8F0] transition-all duration-300 group-hover:ring-2 group-hover:ring-[#F2B705]">
                  <Image
                    src={member.src}
                    alt=""
                    width={288}
                    height={288}
                    quality={80}
                    sizes="144px"
                    className={`h-full w-full object-cover ${member.imgClass}`}
                  />
                </div>
                <div className="mt-5 flex w-full flex-col items-center text-center">
                  <div className="text-lg font-bold text-[#1F1B2E] notranslate">
                    {member.name}
                  </div>
                  <span className="mt-2 inline-block rounded-full bg-[#EEF3FB] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#3D69B0]">
                    {member.role}
                  </span>
                </div>
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#D5DFEF] bg-white px-3.5 py-1.5 text-xs font-semibold text-[#3D69B0] transition-all hover:border-[#3D69B0] hover:bg-[#3D69B0] hover:text-white"
                  aria-label={`${member.name} on LinkedIn`}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.268 2.37 4.268 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </a>
              </div>
            );
            const groupLabel = (text: string) => (
              <div data-animate className="mt-16 flex items-center justify-center gap-3">
                <span className="h-px w-8 bg-[#CBD8EE]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#3D69B0]">
                  {text}
                </span>
                <span className="h-px w-8 bg-[#CBD8EE]" />
              </div>
            );
            return (
              <>
                {groupLabel("Leadership")}
                <div className="mt-8 flex flex-wrap justify-center gap-6">
                  {management.map(renderCard)}
                </div>
                {groupLabel("Brand Ambassadors")}
                <div className="mt-8 flex flex-wrap justify-center gap-6">
                  {ambassadors.map(renderCard)}
                </div>
              </>
            );
          })()}
          </div>
        </section>

        {/* ── Terminal CTA — the scroll ends on a clear ask, not the team grid ── */}
        <section className="w-full bg-[#1F1B2E]">
          <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-6 px-6 py-16 text-center sm:px-8 sm:py-20">
            <h2 data-animate className={DISPLAY_LIGHT}>
              Never do laundry again.
            </h2>
            <p data-animate data-delay="1" className="max-w-md text-base leading-relaxed text-white">
              Book a Tuesday pickup in under a minute. No commitment.
            </p>
            <Link
              data-animate
              data-delay="2"
              href={userEmail ? "/account?scroll=pickup" : "/pickup"}
              className="cta-magnetic group inline-flex items-center gap-2.5 rounded-full bg-[#F2B705] py-4 pl-7 pr-3 text-base font-bold text-[#2E2404] shadow-[0_16px_36px_rgba(242,183,5,0.4)] hover:-translate-y-0.5 hover:bg-[#F6C23D]"
            >
              Schedule a pickup
              <span className="cta-arrow inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2E2404]/15" aria-hidden>&rarr;</span>
            </Link>
          </div>
        </section>

        {/* Footer is rendered globally from app/layout.tsx so it appears on
            every page; nothing needed here. */}
      </main>
    </div>
  );
}
